import { Component, Inject, Input, OnDestroy, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { AppComponent } from '../../../app.component';
import { UserProfileService } from '../../../service/user-profile.service';
import { getTimeZoneStatus, TimeZoneStatus } from '../../Utility/timeZoneStatus';

// =============================================================================
// "Your time zone is not set" banner.
//
// SCOPE, AND WHY THE COPY SAYS WHAT IT SAYS
// Notes are stored and timestamped in UTC. Nothing about a missing time zone
// touches the stored evidence, the signature, or the timestamp itself, and this
// component must never imply otherwise.
//
// It is NOT display-only, though. The same profile field drives the server's
// date rendering for the generated notebook PDF, the full-disclosure package and
// the witness-completion email, all through the same core profile that
// substitutes "UTC" when the value is missing. So the wrong clock time reaches a
// deliverable, not just a card, and the copy says "on screen and in downloaded
// reports" for that reason.
//
// PLACEMENT AND DISMISSAL
// Shown on the Console (the landing surface) and on My Notebooks (where the
// suspect times are actually printed on every card). Dismissal is per browser
// SESSION, not permanent: the condition is still true after a dismissal, and a
// forever-dismiss would hide a live display defect from someone who has already
// forgotten about it. The copy in Settings > Profile is NOT dismissible at all
// ([dismissible]="false"), so the condition can never disappear entirely, and it
// sits next to the control that fixes it.
//
// It never blocks: no modal, no overlay, no focus steal. This product is used
// under time pressure and documenting must always win.
//
// CHANGE DETECTION is deliberately Default rather than OnPush. The profile
// object is mutated in place, by the profile load, by the Settings dropdown's
// ngModel, and by the SignalR TimeZoneUpdated push, none of which are inputs to
// this component. OnPush would leave a stale banner on screen after the user
// fixed the very thing it is complaining about. `status` memoises on the pair of
// field values so the per-cycle cost is a string compare.
// =============================================================================
@Component({
  standalone: true,
  selector: 'app-timezone-warning',
  imports: [CommonModule],
  templateUrl: './timezone-warning.component.html',
  styleUrls: ['./timezone-warning.component.scss'],
})
export class TimezoneWarningComponent implements OnDestroy {

  /** When false the banner has no dismiss control and stays put. Used by the
   *  Settings placement, which is the permanent home of this message. */
  @Input() dismissible: boolean = true;

  /** When false the "Set time zone" button is hidden. The Settings placement
   *  renders directly above the time zone dropdown, so a button that reopens
   *  the page you are already on would be noise. */
  @Input() showAction: boolean = true;

  // Per-session, per-tab. sessionStorage rather than localStorage is the whole
  // point: closing the browser brings the warning back because the problem
  // came back with it.
  private static readonly DISMISS_KEY = 'fn.timezoneWarning.dismissed';

  private dismissed: boolean = false;

  // Memo for `status`. The sentinel cannot collide with a real key.
  private cachedKey: string | null = null;
  private cachedStatus: TimeZoneStatus = 'set';

  // The banner must not accuse a correctly-configured user during the profile
  // load. UserModel initialises timeZone and ianaTimeZoneId to '', which
  // getTimeZoneStatus correctly classifies as NOT SET, so before the profile
  // arrives every user looks misconfigured. The console and notebooks routes
  // carry only MsalGuard, with no profile-init gate, so that window is real on
  // every cold load, and permanent if the profile fetch fails offline.
  private profileLoaded = false;
  private profileSub?: Subscription;

  constructor(
    private userProfileService: UserProfileService,
    // Optional so this component is safe to drop anywhere, including a context
    // where the root component is not on the injector chain (tests, a portalled
    // dialog). Without it the CTA hides instead of throwing.
    @Optional() @Inject(AppComponent) private appComponent: AppComponent | null,
  ) {
    this.dismissed = this.readDismissed();
    // Same signal UserProfileInitGuardGuard already trusts for "profile ready".
    this.profileSub = this.userProfileService?.isInitialized?.subscribe(
      (ready: boolean) => { this.profileLoaded = !!ready; },
    );
  }

  ngOnDestroy(): void {
    this.profileSub?.unsubscribe();
  }

  /** Classification of the CURRENT profile value. */
  get status(): TimeZoneStatus {
    const user: any = this.userProfileService ? this.userProfileService.user : null;
    const key = user ? `${user.timeZone}\u0000${user.ianaTimeZoneId}` : '\u0000';
    if (key !== this.cachedKey) {
      this.cachedKey = key;
      this.cachedStatus = getTimeZoneStatus(user);
    }
    return this.cachedStatus;
  }

  get visible(): boolean {
    // Say nothing until we have actually seen the user's saved value. An unknown
    // time zone is not a wrong one, and a false accusation here trains people to
    // dismiss the banner that matters.
    if (!this.profileLoaded) {
      return false;
    }
    if (this.status === 'set') {
      return false;
    }
    return !this.dismissible || !this.dismissed;
  }

  get canOpenSettings(): boolean {
    return this.showAction && !!this.appComponent;
  }

  /** An id the browser could not resolve is a different fault from a blank one,
   *  and the user needs to know their saved choice is the problem. */
  get title(): string {
    return this.status === 'unresolvable'
      ? 'Your time zone is not recognized'
      : 'Your time zone is not set';
  }

  /** The two facts, in order: what may be wrong, and what is definitely fine.
   *  Do not reword the second sentence without checking that it still says the
   *  stored evidence is unaffected. */
  get body(): string {
    return 'Dates and times shown on screen and in downloaded reports may not match your local clock. '
      + 'Every note is stored and timestamped in UTC, so the recorded times are correct and unaffected.';
  }

  /** Announcement for the shared .cse-sr-only live region. Empty while hidden,
   *  which is what makes the region fire when the banner appears. */
  get liveMessage(): string {
    return this.visible ? `${this.title}. ${this.body}` : '';
  }

  openTimeZoneSetting(): void {
    if (!this.appComponent) {
      return;
    }
    // goSetting() opens the Settings dialog and loads the profile into it;
    // 'profile' is its default tab and holds the time zone dropdown. The
    // explicit onChangeComponent call covers the case where a previous visit
    // left the dialog on another tab.
    this.appComponent.goSetting('profile');
    if (this.appComponent.ejDialogSettings) {
      this.appComponent.ejDialogSettings.onChangeComponent('profile');
    }
  }

  dismiss(): void {
    this.dismissed = true;
    try {
      sessionStorage.setItem(TimezoneWarningComponent.DISMISS_KEY, '1');
    } catch {
      // Private mode or a storage-disabled browser. The dismissal still holds
      // for this component instance; it just will not carry across a reload.
    }
  }

  private readDismissed(): boolean {
    try {
      return sessionStorage.getItem(TimezoneWarningComponent.DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }
}
